import React, { useState, useMemo, useEffect } from "react";
import { Skills } from "./components/Skills";
import { Experience } from "./components/Experience";
import { Education } from "./components/Education";
import { ResumeGap } from "./components/ResumeGap";
import { TimelineList } from "./components/TimelineList";
import { Card } from "@/components/ui/Card";
import { useScrollSpy } from "../hooks/useScrollSpy";
import VerticalSectionNav from "../component/VerticalSectionNav";

const AdditionalSection = ({ section }) => {
  const { entries = [], fields = {} } = section;
  const allEntries = entries.length > 0 ? entries : [fields];

  return (
    <div className="space-y-2">
      {allEntries.map((entryFields) => {
        const items = Object.entries(entryFields)
          .filter(([, value]) => value != null && String(value).trim() !== "")
          .map(([key, value]) => ({ title: key, subtitle: null, duration: null, description: String(value) }));

        const key = Object.values(entryFields).find((v) => v != null && String(v).trim() !== "") ?? JSON.stringify(entryFields);

        return <TimelineList key={key} items={items} dotColor="#7C3AED" />;
      })}
    </div>
  );
};

// The candidate's answers to the job's manual screening questions. Reads the
// per-answer snapshot, so it shows exactly what was asked at apply time even
// if the job's questions changed since.
const AdditionalQA = ({ items = [] }) => (
  <div className="space-y-4">
    {items.map((qa, i) => (
      <div key={i}>
        <p className="text-sm font-medium text-text-primary">{qa.question}</p>
        <p className="text-sm text-text-secondary mt-0.5">
          {qa.answer_type === "YES_NO"
            ? String(qa.answer_value).toLowerCase() === "yes"
              ? "Yes"
              : "No"
            : qa.answer_value || "—"}
        </p>
      </div>
    ))}
  </div>
);

const buildSections = (data, resumeGaps, additionalQa) => {
  const sections = [];
  const seenLabels = new Set();

  const add = (entry) => {
    const key = entry.label.toLowerCase().trim();
    if (seenLabels.has(key)) return;
    seenLabels.add(key);
    sections.push(entry);
  };

  const hasSkills = (Array.isArray(data?.skills) && data.skills.length > 0) || (Array.isArray(data?.languages) && data.languages.length > 0);
  if (hasSkills) add({ id: "skills", label: "Skills & Languages", type: "skills" });

  const hasExperience = Array.isArray(data?.work_experience) && data.work_experience.some((i) => i?.title || i?.company || i?.description);
  if (hasExperience) add({ id: "experience", label: "Experience", type: "experience" });

  const hasEducation = Array.isArray(data?.education) && data.education.some((i) => i?.degree || i?.institution || i?.field_of_study);
  if (hasEducation) add({ id: "education", label: "Education", type: "education" });

  const hasGaps = resumeGaps != null && Array.isArray(resumeGaps.gaps) && resumeGaps.gaps.length > 0;
  if (hasGaps) add({ id: "resume-gaps", label: "Employment Gaps", type: "resume-gaps", resumeGaps });

  const additionalByTitle = new Map();
  (data?.additional_sections || []).forEach((section) => {
    const title = section.section_title?.trim();
    if (!title) return;
    const hasContent = Object.values(section.fields || {}).some((v) => v != null && String(v).trim() !== "");
    if (!hasContent) return;
    if (!additionalByTitle.has(title)) {
      additionalByTitle.set(title, { section_title: title, entries: [section.fields] });
    } else {
      additionalByTitle.get(title).entries.push(section.fields);
    }
  });

  additionalByTitle.forEach((section, title) => {
    const id = `additional-${title.toLowerCase().replace(/\s+/g, "-")}`;
    add({ id, label: title, type: "additional", section });
  });

  // Screening Q&A last — after every resume section, per the placement spec.
  if (Array.isArray(additionalQa) && additionalQa.length > 0) {
    add({ id: "additional-qa", label: "Additional Q&A", type: "additional-qa", additionalQa });
  }

  return sections;
};

const SectionContent = ({ sectionDef, data }) => {
  switch (sectionDef.type) {
    case "skills":
      return <Skills data={data} />;
    case "experience":
      return <Experience data={data} />;
    case "education":
      return <Education data={data} />;
    case "resume-gaps":
      return <ResumeGap resumeGaps={sectionDef.resumeGaps} />;
    case "additional":
      return <AdditionalSection section={sectionDef.section} />;
    case "additional-qa":
      return <AdditionalQA items={sectionDef.additionalQa} />;
    default:
      return null;
  }
};

export const Resume = ({ data, candidateId, resumeGaps, additionalQa }) => {
  const sections = useMemo(() => buildSections(data, resumeGaps, additionalQa), [data, resumeGaps, additionalQa]);
  const sectionIds = useMemo(() => sections.map((s) => s.id), [sections]);

  const { activeId, setActiveId, containerRef, registerSectionRef, scrollToSection } = useScrollSpy(sectionIds);

  // Re-sync to the first section whenever the candidate or section list changes.
  useEffect(() => {
    setActiveId(sections[0]?.id ?? null);
  }, [candidateId, sections, setActiveId]);

  if (sections.length === 0) return null;

  return (
    <div className="flex flex-col h-full min-h-0">
      <Card className="h-full min-h-0 flex flex-col overflow-hidden bg-background-secondary p-0 rounded-none border-none">
        <div className="flex flex-1 min-h-0">
          {sections.length > 1 && (
            <div className="pt-4 pb-5 pl-4 min-h-0 border-r border-border-primary overflow-x-hidden overflow-y-auto custom-scrollbar">
              <VerticalSectionNav sections={sections} activeSection={activeId} onSelect={scrollToSection} />
            </div>
          )}

          <div className="flex-1 min-h-0">
            <div ref={containerRef} className="h-full min-h-0 overflow-y-auto custom-scrollbar px-5 pb-5 pt-4">
              {sections.map((sectionDef) => (
                <div key={sectionDef.id} ref={registerSectionRef(sectionDef.id)} data-section-id={sectionDef.id} className="scroll-mt-2 mt-4">
                  <h3 className="mb-3 text-base font-semibold text-text-primary">{sectionDef.label}</h3>
                  <SectionContent sectionDef={sectionDef} data={data} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default Resume;
